import { Badge } from "@mantine/core";

// ─── Transaction type badge (Income / Expense) ───────────────────────────────

const TRANSACTION_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  INCOME: { label: "Income", color: "green" },
  EXPENSE: { label: "Expense", color: "red" },
};

interface TransactionTypeBadgeProps {
  type: string | null | undefined;
}

export function TransactionTypeBadge({ type }: TransactionTypeBadgeProps) {
  if (!type) return <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;
  const config = TRANSACTION_TYPE_CONFIG[type.toUpperCase()] ?? { label: type, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

// ─── Spending type badge (Need / Want) ───────────────────────────────────────

const SPENDING_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  NEED: { label: "Need", color: "yellow" },
  WANT: { label: "Want", color: "violet" },
};

interface SpendingTypeBadgeProps {
  type: string | null | undefined;
}

export function SpendingTypeBadge({ type }: SpendingTypeBadgeProps) {
  if (!type) return <span style={{ color: "var(--mantine-color-slate-4)" }}>------</span>;
  const config = SPENDING_TYPE_CONFIG[type.toUpperCase()] ?? { label: type, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

// ─── Order / Investment type badge ───────────────────────────────────────────

const ORDER_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  SIP: { label: "SIP", color: "teal" },
  XSIP: { label: "XSIP", color: "teal" },
  STP: { label: "STP", color: "violet" },
  SWP: { label: "SWP", color: "green" },
  TOPUP: { label: "Top-Up", color: "cyan" },
  SPROD: { label: "SPROD", color: "indigo" },
  LUMPSUM: { label: "Lumpsum", color: "blue" },
  SWITCH: { label: "Switch", color: "orange" },
  REDEMPTION: { label: "Redemption", color: "red" },
  PAUSE: { label: "Pause", color: "yellow" },
  // Portal short codes
  P: { label: "Purchase", color: "green" },
  R: { label: "Redemption", color: "red" },
  S: { label: "Switch", color: "orange" },
};

interface OrderTypeBadgeProps {
  type: string | null | undefined;
}

export function OrderTypeBadge({ type }: OrderTypeBadgeProps) {
  if (!type) return <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;
  const config = ORDER_TYPE_CONFIG[type.toUpperCase()] ?? { label: type, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: "Account Created", color: "green" },
  INACTIVE: { label: "Inactive", color: "gray" },
  SUSPENDED: { label: "Suspended", color: "red" },
  suspended: { label: "Suspended", color: "red" },
  PENDING_AUTH: { label: "Email Auth Pending", color: "orange" },
  PENDING_AUTHENTICATION: { label: "Email Auth Pending", color: "orange" },
  PENDING_VERIFICATION: { label: "Profile Under Review", color: "violet" },
  ucc_pan_verification: { label: "PAN Verification", color: "teal" },
  ucc_auth_ucc: { label: "Acc Creation Failed", color: "red" },
  ucc_elog_verification: { label: "E-log Verification", color: "blue" },
  ucc_kyc_verification: { label: "KYC Verification", color: "blue" },
  ucc_bank_acc: { label: "Bank Account", color: "cyan" },
  ucc_auth_elog: { label: "E-log Auth", color: "orange" },
  ucc_auth_nominee: { label: "Nominee Auth", color: "orange" },
};

export const UCC_STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({
  value,
  label,
}));

interface AdminStatusBadgeProps {
  status: string | null;
}

export function AdminStatusBadge({ status }: AdminStatusBadgeProps) {
  if (!status) return <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;
  const config = STATUS_CONFIG[status] ?? { label: status, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

// ─── Order status badge ───────────────────────────────────────────────────────

const ORDER_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: "Pending", color: "yellow" },
  received: { label: "Received", color: "blue" },
  order_2fa_pending: { label: "Order 2FA Pending", color: "orange" },
  bank_tpv_pending: { label: "Bank TPV Pending", color: "orange" },
  payment_pending: { label: "Payment Pending", color: "orange" },
  match_pending: { label: "Match Pending", color: "yellow" },
  matched: { label: "Matched", color: "teal" },
  sent_to_rta: { label: "Sent To RTA", color: "blue" },
  queued_for_rta: { label: "Queued For RTA", color: "blue" },
  rta_reprocess: { label: "RTA Reprocess", color: "orange" },
  rta_resp_rcvd: { label: "RTA Response Received", color: "blue" },
  rta_rejected: { label: "RTA Rejected", color: "red" },
  units_rta_settled: { label: "Units RTA Settled", color: "teal" },
  done: { label: "Done", color: "green" },
  platform_rejected: { label: "Platform Rejected", color: "red" },
  threshold_approval_pending: { label: "Threshold Approval Pending", color: "yellow" },
  ops_rejected: { label: "Ops Rejected", color: "red" },
  redempt_rta_settled: { label: "Redemption RTA Settled", color: "teal" },
  amc_mis_payout_updated: { label: "AMC MIS Payout Updated", color: "cyan" },
  sw_rta_so_valid: { label: "SW RTA SO Valid", color: "green" },
  sw_rta_si_valid: { label: "SW RTA SI Valid", color: "green" },
  cancelled: { label: "Cancelled", color: "red" },
  expired: { label: "Expired", color: "gray" },
};

export const ORDER_STATUS_OPTIONS = Object.entries(ORDER_STATUS_CONFIG).map(([value, { label }]) => ({
  value,
  label,
}));

export const ORDER_TYPE_OPTIONS = [
  { value: "P", label: "Purchase" },
  { value: "R", label: "Redemption" },
  { value: "S", label: "Switch" },
];

interface OrderStatusBadgeProps {
  status: string | null | undefined;
}

export function OrderStatusBadge({ status }: OrderStatusBadgeProps) {
  if (!status) return <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;
  const config = ORDER_STATUS_CONFIG[status] ?? { label: status, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}
